import React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Button,
  Icon,
  Text,
  Badge,
  Tooltip,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  calcularDuracao,
  calcularDiferencaRefeicao,
  calcularDiferencaDescanso,
  calcularHE50,
  formatDate,
  calcularAdicionalNoturno,
  calcularHE100,
  converterParaMinutos,
} from "../helpers/Functions";
import { FaEdit } from "react-icons/fa";
import { MdHistory } from "react-icons/md";

function TabelaAgrupada({ data, logsData }) {
  const navigate = useNavigate();

  // Função para normalizar datas
  const normalizarData = (data) => {
    if (!data) return "";
    // Remove qualquer parte de hora e mantém apenas a data no formato YYYY-MM-DD
    return data.split("T")[0].split(" ")[0];
  };

  // Função para verificar se um dia tem alterações
  const temAlteracoes = (data) => {
    if (!logsData || !data) return false;
    const dataNormalizada = normalizarData(data);
    return logsData.some((log) => {
      const logData = normalizarData(log.data_original);
      return logData === dataNormalizada;
    });
  };

  // Função para formatar o texto do badge para exibição
  const formatarTextoBadge = (texto) => {
    if (!texto) return texto;
    const textoLower = texto.trim().toLowerCase();

    // Casos especiais que precisam de formatação específica
    if (textoLower === "dsr_noutrocidade") {
      return "DSR_NOUTRACIDADE";
    }

    // Para outros casos, retorna o texto original
    return texto;
  };

  // Agrupar os dados pela propriedade "data_inicial"
  const agrupadosPorData = data.reduce((acc, item) => {
    if (!acc[item.data_inicial]) {
      acc[item.data_inicial] = [];
    }
    acc[item.data_inicial].push(item);
    return acc;
  }, {});

  console.log("agrupadosPorData", agrupadosPorData);

  const handleEdit = (dataInicial, eventos) => {
    // Filtra os logs do dia selecionado, garantindo formato AAAA-MM-DD
    const normalizarData = (data) =>
      data ? data.split("T")[0].split(" ")[0] : "";
    const logsDoDia = logsData
      ? logsData.filter(
          (log) =>
            normalizarData(log.data_original) === normalizarData(dataInicial)
        )
      : [];
    navigate(`/editar/${dataInicial}/`, {
      state: { eventos, logsDoDia },
    });
  };

  // Função para converter horas no formato "HH:mm" em minutos
  const converterHorasParaMinutos = (horas) => {
    if (
      !horas ||
      typeof horas !== "string" ||
      !/^(-)?\d{1,2}:\d{2}$/.test(horas)
    ) {
      console.warn("⛔ Valor de hora inválido para conversão:", horas);
      return 0;
    }
    const negativo = horas.startsWith("-");
    const [h, m] = horas.replace("-", "").split(":").map(Number);
    const total = h * 60 + m;
    return negativo ? -total : total;
  };

  // Função para converter minutos de volta para o formato "HH:mm"
  const converterMinutosParaHoras = (minutos) => {
    if (!minutos || isNaN(minutos)) return "00:00"; // Se minutos for null, undefined ou inválido, retorna "00:00"

    const isNegativo = minutos < 0;
    const minutosAbsolutos = Math.abs(minutos);
    const h = Math.floor(minutosAbsolutos / 60);
    const m = minutosAbsolutos % 60;
    const resultado = `${h}:${m.toString().padStart(2, "0")}`;

    return isNegativo ? `-${resultado}` : resultado;
  };
  // Função para calcular os totais

  // const totais = calcularTotais(agrupadosPorData);

  const getUltimaHoraFinal = (eventos) => {
    if (!eventos || eventos.length === 0) return null;

    // Ordenar por hora_inicial só para garantir a sequência correta
    const eventosOrdenados = [...eventos].sort(
      (a, b) =>
        converterHorasParaMinutos(a.hora_inicial) -
        converterHorasParaMinutos(b.hora_inicial)
    );

    return eventosOrdenados[eventosOrdenados.length - 1]?.hora_final || null;
  };

  const getPrimeiraHoraInicial = (eventos) => {
    if (!eventos || eventos.length === 0) return null;

    // Ordenar eventos para garantir a sequência correta
    const eventosOrdenados = [...eventos].sort(
      (a, b) =>
        converterHorasParaMinutos(a.hora_inicial) -
        converterHorasParaMinutos(b.hora_inicial)
    );

    return eventosOrdenados[0]?.hora_inicial || null;
  };

  const calcularIntersticio = (
    eventosAtuais,
    eventosProximoDia,
    dataAtual,
    dataProximoDia
  ) => {
    if (!eventosAtuais || !eventosProximoDia) return "";

    const ultimaHoraFinal = getUltimaHoraFinal(eventosAtuais); // Ex: 21:16
    const primeiraHoraInicial = getPrimeiraHoraInicial(eventosProximoDia); // Ex: 07:32

    if (!ultimaHoraFinal || !primeiraHoraInicial) return "";

    const minutosFinal = converterHorasParaMinutos(ultimaHoraFinal); // Converte 21:16 para minutos (1276 min)
    const minutosInicial = converterHorasParaMinutos(primeiraHoraInicial); // Converte 07:32 para minutos (452 min)

    // Verificar se estamos comparando corretamente o dia anterior com o dia seguinte
    if (dataAtual >= dataProximoDia) {
      console.error(
        "Erro: Datas invertidas. Certifique-se de que dataAtual < dataProximoDia."
      );
      return "";
    }

    // Cálculo correto do interstício (tempo entre o final do dia atual e o início do próximo)
    let diferenca = minutosInicial + (24 * 60 - minutosFinal);

    // console.log(`Interstício entre ${dataAtual} e ${dataProximoDia}:`);
    // console.log(
    //   `- Final (${dataAtual}): ${ultimaHoraFinal} (${minutosFinal} min)`
    // );
    // console.log(
    //   `- Início (${dataProximoDia}): ${primeiraHoraInicial} (${minutosInicial} min)`
    // );
    // console.log(
    //   `- Diferença calculada: ${diferenca} min (${converterMinutosParaHoras(
    //     diferenca
    //   )})`
    // );
    // console.log("------------------------------------------------------");

    return converterMinutosParaHoras(diferenca);
  };

  function calcularHE50Linha(eventos) {
    if (!eventos || eventos.length === 0) return "";
    const semana = eventos[0].semana_texto;
    const obs = eventos[0].obs_compensada?.trim().toLowerCase();
    const isFeriado = obs === "feriado";
    const isDiaEspecial = [
      "dsr_cg",
      "dsr_noutrocidade",
      "falta",
      "afastamento",
      "ferias",
    ].includes(obs || "");
    const isNaoTrabalhadoComCompensacao =
      (!eventos[0].hora_inicial || eventos[0].hora_inicial === ":") &&
      (!eventos[0].hora_final || eventos[0].hora_final === ":") &&
      eventos[0].data_compensada?.trim() !== "";

    if (isNaoTrabalhadoComCompensacao) return "";
    if (isDiaEspecial) return "";
    if (isFeriado) return "00:00";

    const duracao = calcularDuracao(
      eventos[0].hora_inicial,
      eventos[eventos.length - 1].hora_final
    );

    if (duracao === null && !eventos[0].obs_compensada?.trim()) {
      return semana === "Sabado" ? "-04:00" : "-08:00";
    }
    if (duracao === null) {
      return "00:00";
    }
    if (semana === "Domingo" && !eventos[0].data_compensada?.trim()) {
      return "00:00";
    }

    const valor = calcularHE50(
      duracao,
      calcularDiferencaRefeicao(eventos),
      calcularDiferencaDescanso(eventos),
      semana === "Sabado" ? "4:00" : "8:00",
      calcularAdicionalNoturno(eventos)
    );
    if (valor === "-8:00") return "";
    return valor || "00:00";
  }

  const calcularTotais = (agrupadosPorData, diasOrdenados) => {
    const totais = {
      jornada: 0,
      normal: 0,
      refeicao: 0,
      descanso: 0,
      he50: 0,
      he100: 0,
      adicionalNoturno: 0,
      intersticio: 0,
    };

    Object.entries(agrupadosPorData).forEach(
      ([dataInicial, eventos], index) => {
        const obs = eventos[0].obs_compensada?.trim().toLowerCase();
        const semana = eventos[0].semana_texto;
        const isFeriado = obs === "feriado";

        const jornada = calcularDuracao(
          eventos[0].hora_inicial,
          eventos[eventos.length - 1].hora_final
        );
        const refeicao = calcularDiferencaRefeicao(eventos);
        const descanso = calcularDiferencaDescanso(eventos);
        let adicionalNoturno = calcularAdicionalNoturno(eventos);

        // ⛔ Dias especiais que devem ser ignorados
        const isDiaEspecial = [
          "dsr_noutrocidade",
          "falta",
          "afastamento",
          "ferias",
          "dsr_cg",
        ].includes(obs);

        // Só ignora se for dia especial, não por jornada ser 00:00
        if (isDiaEspecial) {
          console.log(
            `⛔ Ignorado: ${dataInicial} ${semana} ${obs} - Dia especial`
          );
          return;
        }

        const isDomingo = semana === "Domingo";
        const isCompensado = eventos[0].data_compensada?.trim() !== "";

        // HE100 só aplica em domingo/feriado quando há jornada > 0
        const he100Aplica =
          ((isDomingo && !isCompensado) || isFeriado) &&
          converterParaMinutos(jornada) > 0;
        let he100 = he100Aplica
          ? calcularHE100(
              jornada,
              refeicao,
              descanso,
              adicionalNoturno,
              isDomingo,
              isFeriado
            )
          : "00:00";

        const he50 = calcularHE50Linha(eventos);

        // Para jornadas nulas ou 00:00, força adicional noturno como 00:00
        if (jornada === "00:00" || jornada === null) {
          console.log(
            `🔧 Forçando adicional noturno para 00:00 em ${dataInicial} (jornada: ${jornada})`
          );
          adicionalNoturno = "00:00";
        }

        // Para o log detalhado, calcular localmente os valores do interstício
        const eventosDiaAnterior =
          index > 0 ? agrupadosPorData[diasOrdenados[index - 1]] : [];
        const ultimaHoraFinalLog =
          eventosDiaAnterior.length > 0
            ? getUltimaHoraFinal(eventosDiaAnterior)
            : null;
        const primeiraHoraInicialLog =
          eventos.length > 0 ? getPrimeiraHoraInicial(eventos) : null;
        // Debug: log dos valores calculados
        console.log(
          `Dia: ${dataInicial} | Jornada: ${jornada} | Normal: ${
            semana === "Sabado" ? "4:00" : "8:00"
          } | Refeição: ${refeicao} | Descanso: ${descanso} | HE50: ${he50} | HE100: ${he100} | Ad. Noturno: ${adicionalNoturno}`
        );
        // Log detalhado dos totais acumulados
        console.log(
          `[TOTALS] ${dataInicial} | +jornada: ${converterParaMinutos(
            jornada
          )} | +normal: ${
            semana === "Sabado" ? 240 : 480
          } | +refeicao: ${converterParaMinutos(
            refeicao
          )} | +descanso: ${converterParaMinutos(
            descanso
          )} | +he50: ${converterParaMinutos(
            he50
          )} | +he100: ${converterParaMinutos(
            he100
          )} | +adNoturno: ${converterParaMinutos(
            adicionalNoturno
          )} | +intersticio: ${
            index > 0
              ? ultimaHoraFinalLog &&
                primeiraHoraInicialLog &&
                ultimaHoraFinalLog !== ":" &&
                primeiraHoraInicialLog !== ":"
                ? calcularIntersticio(
                    eventosDiaAnterior,
                    eventos,
                    diasOrdenados[index - 1],
                    dataInicial
                  )
                : "-"
              : "-"
          }`
        );

        // Apenas log de aviso para valores negativos
        if (converterHorasParaMinutos(he50) < 0) {
          console.warn(`⚠️ HE50 negativo em ${dataInicial}: ${he50}`);
        }
        if (converterHorasParaMinutos(he100) < 0) {
          console.warn(`⚠️ HE100 negativo em ${dataInicial}: ${he100}`);
        }

        // Interstício
        if (index > 0) {
          const eventosDiaAnterior =
            agrupadosPorData[diasOrdenados[index - 1]] || [];
          const ultimaHoraFinal = getUltimaHoraFinal(eventosDiaAnterior);
          const primeiraHoraInicial = getPrimeiraHoraInicial(eventos);

          if (
            ultimaHoraFinal &&
            primeiraHoraInicial &&
            ultimaHoraFinal !== ":" &&
            primeiraHoraInicial !== ":"
          ) {
            const intersticio = calcularIntersticio(
              eventosDiaAnterior,
              eventos,
              diasOrdenados[index - 1],
              dataInicial
            );

            if (
              intersticio &&
              intersticio !== ":" &&
              intersticio !== "" &&
              converterParaMinutos(intersticio) <= 24 * 60
            ) {
              totais.intersticio += converterParaMinutos(intersticio);
            }
          }
        }

        console.log(
          `📅 ${dataInicial} | ${semana} | Jornada: ${jornada} | HE100: ${he100} | HE50: ${he50}`
        );

        totais.jornada += converterParaMinutos(jornada);
        totais.normal += semana === "Sabado" ? 240 : 480;
        totais.refeicao += converterParaMinutos(refeicao);
        totais.descanso += converterParaMinutos(descanso);
        // Total HE50: soma exatamente o que é exibido na coluna HE50
        totais.he50 += converterParaMinutos(he50);
        totais.he100 += converterParaMinutos(he100);

        const adNoturnoMinutos = converterParaMinutos(adicionalNoturno);
        console.log(
          `📊 [${dataInicial}] Adicional Noturno: ${adicionalNoturno} -> ${adNoturnoMinutos} minutos | Total atual: ${totais.adicionalNoturno}`
        );
        totais.adicionalNoturno += adNoturnoMinutos;
      }
    );

    console.log("🔥 Totais finais calculados:", totais);
    return totais;
  };

  const diasOrdenados = Object.keys(agrupadosPorData).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  const totais = calcularTotais(agrupadosPorData, diasOrdenados);

  return (
    <Box
      overflowX="auto"
      p={4}
      bg="white"
      borderRadius="lg"
      boxShadow="lg"
      maxW="100vw"
    >
      <Table
        size="sm"
        variant="simple"
        borderRadius="lg"
        boxShadow="md"
        minW="900px"
      >
        <Thead position="sticky" top={0} zIndex={1} bg="red.600">
          <Tr>
            <Th color="white">Dia</Th>
            <Th color="white">Dia/Semana</Th>
            <Th color="white">H Início</Th>
            <Th color="white">H Fim</Th>
            <Th color="white">H Jornada</Th>
            <Th color="white">H Normal</Th>
            <Th color="white">Refeição</Th>
            <Th color="white">Descanso</Th>
            <Th color="white">HE 50%</Th>
            <Th color="white">HE 100%</Th>
            <Th color="white">Ad. Noturno</Th>
            <Th color="white">Observação</Th>
            <Th color="white">CP</Th>
            <Th color="white">Interstício</Th>
            <Th color="white">Placa</Th>
            <Th color="white">Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {Object.entries(agrupadosPorData).map(
            ([dataInicial, eventos], index) => {
              const isDomingo = eventos[0].semana_texto === "Domingo";
              const isFeriado = ["feriado"].includes(
                eventos[0].obs_compensada?.trim().toLowerCase()
              );

              const rowBg = isFeriado
                ? "yellow.100"
                : isDomingo
                ? "red.100"
                : index % 2 === 0
                ? "gray.50"
                : "white";
              // Encontrar o próximo dia com data_inicial diferente

              const isNaoTrabalhadoComCompensacao =
                (!eventos[0].hora_inicial || eventos[0].hora_inicial === ":") &&
                (!eventos[0].hora_final || eventos[0].hora_final === ":") &&
                eventos[0].data_compensada?.trim() !== "";
              const isDiaEspecial = eventos.every((ev) =>
                [
                  "dsr_cg",
                  "dsr_noutrocidade",
                  "falta",
                  "afastamento",
                  "ferias",
                ].includes(ev.obs_compensada?.trim().toLowerCase() || "")
              );

              const duracao = calcularDuracao(
                eventos[0].hora_inicial,
                eventos[eventos.length - 1].hora_final
              );

              return (
                <Tr
                  key={dataInicial}
                  bg={rowBg}
                  _hover={{ bg: "gray.200" }}
                  transition="background 0.2s"
                >
                  <Td fontWeight="bold">
                    <HStack spacing={2} align="center">
                      <Text>{formatDate(dataInicial)}</Text>
                      {temAlteracoes(dataInicial) && (
                        <Tooltip
                          label="Este dia possui alterações registradas"
                          fontSize="xs"
                          bg="white"
                          color="gray.700"
                          p={2}
                          borderRadius="md"
                          boxShadow="sm"
                          placement="top"
                          hasArrow
                        >
                          <Box>
                            <Badge
                              colorScheme="purple"
                              fontSize="xs"
                              px={1.5}
                              py={0.5}
                              borderRadius="full"
                            >
                              <MdHistory size={12} />
                            </Badge>
                          </Box>
                        </Tooltip>
                      )}
                    </HStack>
                  </Td>
                  <Td>{eventos[0].semana_texto}</Td>
                  <Td>
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? ""
                      : eventos[0].hora_inicial !== ":"
                      ? eventos[0].hora_inicial
                      : "00:00"}
                  </Td>
                  <Td>
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? ""
                      : eventos[eventos.length - 1].hora_inicial !== ":"
                      ? eventos[eventos.length - 1].hora_final
                      : "00:00"}
                  </Td>
                  <Td>
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? ""
                      : calcularDuracao(
                          eventos[0].hora_inicial,
                          eventos[eventos.length - 1].hora_final
                        ) === null
                      ? "00:00" // Dia não trabalhado (campo em branco)
                      : calcularDuracao(
                          eventos[0].hora_inicial,
                          eventos[eventos.length - 1].hora_final
                        ) === "00:00"
                      ? "00:00" // Dia trabalhado sem horas extras
                      : calcularDuracao(
                          eventos[0].hora_inicial,
                          eventos[eventos.length - 1].hora_final
                        )}
                  </Td>
                  <Td className="h normal">
                    {(() => {
                      const duracao = calcularDuracao(
                        eventos[0].hora_inicial,
                        eventos[eventos.length - 1].hora_final
                      );

                      const valorPadrao =
                        eventos[0].semana_texto === "Sabado"
                          ? "04:00"
                          : "08:00";

                      if (isNaoTrabalhadoComCompensacao) {
                        return "";
                      }
                      // 🟡 Se for dia especial, retorna vazio
                      if (isDiaEspecial) {
                        return "";
                      }

                      // 🔴 Se não trabalhou (sem horário), zera
                      if (duracao === null) {
                        return eventos[0].semana_texto === "Sabado"
                          ? "00:00"
                          : "08:00";
                      }

                      // 🔵 Se for domingo e não for compensado, zera
                      if (
                        eventos[0].semana_texto === "Domingo" &&
                        eventos[0].data_compensada?.trim() === ""
                      ) {
                        return "00:00";
                      }

                      // ✅ Caso normal
                      return valorPadrao;
                    })()}
                  </Td>
                  <Td>
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? ""
                      : calcularDuracao(
                          eventos[0].hora_inicial,
                          eventos[eventos.length - 1].hora_final
                        ) === null
                      ? "00:00" // Dia não trabalhado (campo em branco)
                      : calcularDiferencaRefeicao(eventos) === "00:00"
                      ? "00:00" // Dia trabalhado sem refeição
                      : calcularDiferencaRefeicao(eventos) || ""}
                  </Td>
                  <Td>
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? ""
                      : calcularDuracao(
                          eventos[0].hora_inicial,
                          eventos[eventos.length - 1].hora_final
                        ) === null
                      ? "00:00" // Dia não trabalhado (campo em branco)
                      : calcularDiferencaDescanso(eventos) === "00:00"
                      ? "00:00" // Dia trabalhado sem descanso
                      : calcularDiferencaDescanso(eventos) || ""}
                  </Td>
                  <Td className="he50">{calcularHE50Linha(eventos)}</Td>
                  <Td className="he100">
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? "" // 1. Dia especial => vazio
                      : isFeriado
                      ? calcularHE100(
                          duracao,
                          calcularDiferencaRefeicao(eventos),
                          calcularDiferencaDescanso(eventos),
                          calcularAdicionalNoturno(eventos),
                          isDomingo,
                          isFeriado
                        )
                      : duracao === null || duracao === "00:00"
                      ? "00:00" // 2. Sem jornada => "00:00"
                      : isDomingo && eventos[0].data_compensada?.trim() !== "" // 3. Domingo compensado com jornada
                      ? "00:00"
                      : calcularHE100(
                          duracao,
                          calcularDiferencaRefeicao(eventos),
                          calcularDiferencaDescanso(eventos),
                          calcularAdicionalNoturno(eventos),
                          isDomingo,
                          isFeriado
                        )}
                  </Td>

                  <Td className="ad noturno">
                    {isNaoTrabalhadoComCompensacao
                      ? ""
                      : isDiaEspecial
                      ? ""
                      : calcularDuracao(
                          eventos[0].hora_inicial,
                          eventos[eventos.length - 1].hora_final
                        ) === null
                      ? "00:00" // Dia não trabalhado (campo em branco)
                      : calcularAdicionalNoturno(eventos) === "00:00"
                      ? "00:00" // Dia trabalhado sem adicional noturno
                      : calcularAdicionalNoturno(eventos) || "N/A"}
                  </Td>
                  <Td className="observacao">
                    {eventos[0].obs_compensada ? (
                      <Tooltip label={eventos[0].obs_compensada} fontSize="sm">
                        <Badge
                          colorScheme={
                            eventos[0].obs_compensada?.trim().toLowerCase() ===
                            "feriado"
                              ? "yellow"
                              : eventos[0].obs_compensada
                                  ?.trim()
                                  .toLowerCase() === "dsr_cg" ||
                                eventos[0].obs_compensada
                                  ?.trim()
                                  .toLowerCase() === "dsr_noutrocidade"
                              ? "blue"
                              : eventos[0].obs_compensada
                                  ?.trim()
                                  .toLowerCase() === "falta"
                              ? "red"
                              : eventos[0].obs_compensada
                                  ?.trim()
                                  .toLowerCase() === "afastamento"
                              ? "purple"
                              : eventos[0].obs_compensada
                                  ?.trim()
                                  .toLowerCase() === "ferias"
                              ? "green"
                              : "gray"
                          }
                          maxW="150px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {formatarTextoBadge(eventos[0].obs_compensada)}
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Text color="gray.400" fontSize="sm">
                        —
                      </Text>
                    )}
                  </Td>
                  {(() => {
                    // Se for férias, não exibir nada na coluna CP
                    if (
                      eventos[0].obs_compensada?.trim().toLowerCase() ===
                      "ferias"
                    ) {
                      return <Td></Td>;
                    }

                    // Lógica original para outros casos
                    if (
                      (eventos[0].data_compensada &&
                        eventos[0].data_compensada !== dataInicial) ||
                      eventos[0].data_orig_compensacao
                    ) {
                      return (
                        <Td className="cp">
                          {eventos[0].data_compensada &&
                          eventos[0].data_compensada !== dataInicial
                            ? formatDate(eventos[0].data_compensada)
                            : eventos[0].data_orig_compensacao
                            ? formatDate(eventos[0].data_orig_compensacao)
                            : ""}
                        </Td>
                      );
                    } else {
                      return <Td></Td>;
                    }
                  })()}
                  <Td className="intersticio">
                    {index === 0
                      ? "" // Ignora o primeiro dia, pois não há interstício anterior
                      : (() => {
                          const eventosDiaAnterior =
                            agrupadosPorData[diasOrdenados[index - 1]] || [];
                          const eventosHoje = eventos;

                          // Pegamos a última hora final do dia anterior
                          const ultimaHoraFinal =
                            eventosDiaAnterior.length > 0
                              ? getUltimaHoraFinal(eventosDiaAnterior)
                              : null;

                          // Pegamos a primeira hora inicial do dia atual
                          const primeiraHoraInicial =
                            eventosHoje.length > 0
                              ? getPrimeiraHoraInicial(eventosHoje)
                              : null;

                          // 🛑 Se qualquer um dos valores for inválido, não calcular
                          if (
                            !ultimaHoraFinal ||
                            ultimaHoraFinal === ":" ||
                            !primeiraHoraInicial ||
                            primeiraHoraInicial === ":"
                          ) {
                            console.warn(
                              `⚠️ Interstício inválido para ${dataInicial} - Um dos valores é nulo.`
                            );
                            return ""; // Retorna vazio e evita erro
                          }

                          // Calcula o interstício entre o dia anterior e o dia atual
                          const intersticio = calcularIntersticio(
                            eventosDiaAnterior,
                            eventosHoje,
                            diasOrdenados[index - 1],
                            dataInicial
                          );

                          // 🛑 Se o interstício ultrapassar 24 horas ou for inválido, não exibir nada
                          if (
                            !intersticio ||
                            converterParaMinutos(intersticio) > 24 * 60
                          ) {
                            console.warn(
                              `⚠️ Interstício inválido calculado para ${dataInicial}: ${intersticio}`
                            );
                            return "";
                          }

                          // Verifica se o valor do interstício é menor que 11:00
                          const isMenorQue11 =
                            converterParaMinutos(intersticio) <
                            converterParaMinutos("11:00");

                          // Retorna o valor estilizado
                          return (
                            <Text
                              as="span"
                              fontWeight={isMenorQue11 ? "bold" : "normal"}
                              color={isMenorQue11 ? "red.500" : "inherit"}
                            >
                              {isDiaEspecial ? "" : intersticio}
                            </Text>
                          );
                        })()}
                  </Td>
                  <Td fontSize="sm" color="gray.700">
                    {[...new Set(eventos.map((e) => e.placa))].join(", ")}
                  </Td>
                  <Td>
                    <Tooltip label="Editar apontamentos" fontSize="sm">
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleEdit(dataInicial, eventos)}
                        leftIcon={
                          <Icon as={FaEdit} w={4} h={4} color="white" />
                        }
                        borderRadius="full"
                        px={3}
                      >
                        Editar
                      </Button>
                    </Tooltip>
                  </Td>
                </Tr>
              );
            }
          )}
          {/* Linha de Totalizador */}
          <Tr bg="red.600" color="white">
            <Td colSpan={4} fontWeight="bold" fontSize="md">
              Total
            </Td>
            <Td></Td>
            <Td></Td>
            <Td></Td>
            <Td></Td>
            <Td fontWeight="bold" fontSize="md">
              {converterMinutosParaHoras(totais.he50)}
            </Td>
            <Td fontWeight="bold" fontSize="md">
              {converterMinutosParaHoras(totais.he100)}
            </Td>
            <Td fontWeight="bold" fontSize="md">
              {converterMinutosParaHoras(totais.adicionalNoturno)}
            </Td>
            <Td></Td>
            <Td></Td>
            <Td></Td>
            <Td></Td>
            <Td></Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
}

export default TabelaAgrupada;
